/**
 * ErrorBoundary — the difference between "something broke" and a blank app.
 *
 * Before this, nothing in the tree caught a render throw: React 18 unmounts
 * the whole app, leaving the window background (navy on native) and no way
 * out. Force-quitting does not help, because the same data reproduces it.
 * On a paid app that is a refund with no diagnostic trail, and because Play
 * Console only reports NATIVE crashes, a JS white screen affecting every
 * install looks like a perfectly healthy app in the dashboard.
 *
 * Two levels are intended:
 *   - one wrapping the whole app, so a boot-time throw still renders chrome
 *   - one inside the route Suspense, so a single bad screen does not take
 *     the shell (and the tab bar) down with it
 *
 * `resetKey` lets the route-level boundary clear itself on navigation:
 * without it, a screen that threw once stays broken until a full reload.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Changing this value clears a caught error (pass the route path). */
  resetKey?: string;
  /** "screen" renders inline; "app" renders a full-page fallback. */
  variant?: 'app' | 'screen';
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep it in logcat/devtools at minimum. There is no crash reporter
    // wired up yet, so this console line is the only forensic trail a
    // support request can quote.
    // eslint-disable-next-line no-console
    console.error('[DraftEdge] render error:', error, info.componentStack);
  }

  private reload = () => {
    // replace(), not reload(): a bad route in the URL would otherwise throw
    // again immediately. Send them to the app root.
    window.location.replace(
      (import.meta.env.BASE_URL || '/').replace(/\/?$/, '/'),
    );
  };

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const full = this.props.variant !== 'screen';
    return (
      <div
        role="alert"
        className={
          full
            ? 'flex min-h-screen flex-col items-center justify-center gap-4 bg-surface px-6 text-center'
            : 'flex flex-col items-center gap-3 rounded-card bg-surface-raised px-5 py-8 text-center'
        }
      >
        <h1 className="text-lg font-semibold text-ink">Something went wrong</h1>
        <p className="max-w-xs text-sm text-ink-muted">
          This screen hit an error. Your drafts and cheat sheets are saved.
        </p>
        <button
          type="button"
          onClick={this.reload}
          className="rounded-pill bg-accent px-5 py-2.5 text-sm font-semibold text-surface"
        >
          Reload DraftEdge
        </button>
      </div>
    );
  }
}
